import React, { useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import {
  Button, Container, Typography, Box, Alert, Card, CardContent, Grid, Chip, CircularProgress, Stack, Fade, IconButton, Tooltip
} from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as UnlockIcon,
  AccessTime as TimeIcon,
  Token as TokenIcon,
  Refresh as RefreshIcon,
  WifiOff as ConnectionErrorIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeProvider } from '@mui/material/styles';
import { useWallet } from '../context/WalletContext';
import { 
  NETWORKS, 
  fetchUserLocks, 
  fetchTokenSymbol, 
  fetchTokenDecimals, 
  PLATFORM_FEE_WALLET_ETH, 
  PLATFORM_FEE_WALLET_BSC,
  DEVELOPER_FEE_WALLET_ETH,
  DEVELOPER_FEE_WALLET_BSC,
  PLATFORM_FEE_PERCENTAGE,
  DEVELOPER_FEE_PERCENTAGE,
  USER_RECEIVES_PERCENTAGE
} from '../utils/contractUtils';
import { themeCustom, GradientPaper, HeaderBar } from '../utils/themeUtils';

export default function LockedTokens() {
  const {
    provider,
    signer,
    address,
    isConnected,
    contract,
    currentNetwork,
    networkConfig,
    wallets,
    activeWallet,
    switchWallet,
    handleNetworkChange,
    connectWallet,
    disconnectWallet,
    isConnecting,
    connectionStatus,
    isInitialized,
    initError
  } = useWallet();

  const [lockedTokens, setLockedTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState({});
  const [alert, setAlert] = useState({ type: 'info', message: '' });
  const [showAlert, setShowAlert] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Check for pre-loaded data from Dashboard
  useEffect(() => {
    const preloadedData = sessionStorage.getItem('dashboardLockedTokens');
    if (preloadedData && isConnected && address) {
      try {
        const parsedData = JSON.parse(preloadedData);
        // Only use pre-loaded data if it's for the same address
        if (parsedData.length > 0 && parsedData[0].owner?.toLowerCase() === address.toLowerCase()) {
          console.log('Using pre-loaded locked tokens data');
          setLockedTokens(parsedData);
          setIsInitialLoad(false);
          // Clear the pre-loaded data after using it
          sessionStorage.removeItem('dashboardLockedTokens');
          return;
        }
      } catch (error) {
        console.error('Error parsing pre-loaded data:', error);
      }
    }
    
    // If no pre-loaded data, fetch normally
    if (isConnected && contract && provider) {
      fetchLockedTokens(true);
    }
  }, [isConnected, contract, provider, address]);

  // Enhanced fetch locked tokens with better error handling
  const fetchLockedTokens = useCallback(async (forceRefresh = false) => {
    console.log('=== fetchLockedTokens called ===');
    console.log('isConnected:', isConnected);
    console.log('contract:', contract);
    console.log('provider:', provider);
    console.log('address:', address);
    
    if (!isConnected || !contract || !provider) {
      console.log('Not connected or contract not available');
      return;
    }
    
    setLoading(true);
    setIsRefreshing(true);
    
    try {
      console.log('Fetching locked tokens...');
      
      // Get total number of locks
      console.log('Calling contract.lockCount()...');
      const lockCount = await contract.lockCount();
      console.log(`Total locks: ${lockCount.toString()}`);
      
      if (lockCount.toString() === '0') {
        setLockedTokens([]);
        setLoading(false);
        setIsRefreshing(false);
        setIsInitialLoad(false);
        return;
      }
      
      // Only fetch recent locks for better performance (last 100)
      const maxLocksToFetch = 100;
      const startIndex = Math.max(0, lockCount - maxLocksToFetch);
      const locksToFetch = Math.min(lockCount, maxLocksToFetch);
      
      console.log(`Fetching recent locks from ${startIndex} to ${startIndex + locksToFetch - 1}`);
      
      // Fetch locks in batches for better performance
      const batchSize = 20;
      const allLocks = [];
      
      for (let batchStart = startIndex; batchStart < startIndex + locksToFetch; batchStart += batchSize) {
        const batchEnd = Math.min(batchStart + batchSize, startIndex + locksToFetch);
        const batchPromises = [];
        
        for (let i = batchStart; i < batchEnd; i++) {
          batchPromises.push(contract.locks(i));
        }
        
        const batchResults = await Promise.all(batchPromises);
        batchResults.forEach((lock, index) => {
          allLocks.push({ ...lock, lockId: batchStart + index });
        });
      }
      
      console.log(`Fetched ${allLocks.length} locks`);
      
      // Filter ONLY user's locks
      const userLocks = allLocks.filter(lock => 
        lock.owner.toLowerCase() === address.toLowerCase() && !lock.withdrawn
      );
      
      console.log(`Found ${userLocks.length} locked tokens for user`);
      
      if (userLocks.length === 0) {
        setLockedTokens([]);
        setLoading(false);
        setIsRefreshing(false);
        setIsInitialLoad(false);
        return;
      }
      
      // Get unique token addresses for this user only
      const uniqueTokenAddresses = [...new Set(userLocks.map(lock => lock.token))];
      console.log(`Unique tokens for user: ${uniqueTokenAddresses.length}`);
      
      // Fetch token details in parallel with caching
      const tokenDetailsPromises = uniqueTokenAddresses.map(async (tokenAddress) => {
        try {
          const tokenContract = new ethers.Contract(
            tokenAddress, 
            ['function symbol() view returns (string)', 'function decimals() view returns (uint8)'], 
            provider
          );
          
          const [symbol, decimals] = await Promise.all([
            tokenContract.symbol(),
            tokenContract.decimals()
          ]);
          
          return { tokenAddress, symbol, decimals };
        } catch (error) {
          console.error(`Error fetching token details for ${tokenAddress}:`, error);
          return { 
            tokenAddress, 
            symbol: tokenAddress.slice(0, 6) + '...' + tokenAddress.slice(-4), 
            decimals: 18 
          };
        }
      });
      
      const tokenDetailsResults = await Promise.all(tokenDetailsPromises);
      
      // Create token details map
      const tokenDetailsMap = {};
      tokenDetailsResults.forEach(({ tokenAddress, symbol, decimals }) => {
        tokenDetailsMap[tokenAddress] = { symbol, decimals };
      });
      
      // Process user's locks
      const tokensWithDetails = userLocks.map(lock => {
        const tokenDetails = tokenDetailsMap[lock.token];
        const formattedAmount = ethers.utils.formatUnits(lock.amount, tokenDetails.decimals);
        const unlockTime = new Date(lock.unlockTime * 1000);
        const isUnlockable = Date.now() >= unlockTime.getTime();
        
        return {
          ...lock,
          symbol: tokenDetails.symbol,
          decimals: tokenDetails.decimals,
          formattedAmount,
          unlockTime,
          isUnlockable
        };
      });
      
      console.log('Final tokens with details:', tokensWithDetails);
      setLockedTokens(tokensWithDetails);
      setIsInitialLoad(false);
      console.log('Locked tokens fetched successfully');
      
    } catch (error) {
      console.error('Error fetching locked tokens:', error);
      setError(error.message || 'Failed to fetch locked tokens. Please try again.');
      setIsInitialLoad(false);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [isConnected, contract, provider, address]);

  // Clear state when account changes
  useEffect(() => {
    setLockedTokens([]);
    setLastFetchTime(0);
    setIsInitialLoad(true);
  }, [address]);

  // Alert animation
  useEffect(() => {
    if (alert.message) setShowAlert(true);
    if (alert.message && alert.type === 'success') {
      const timer = setTimeout(() => setShowAlert(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  // Handle error and success messages
  useEffect(() => {
    if (error) {
      setAlert({ type: 'error', message: error });
      setError(null);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      setAlert({ type: 'success', message: success });
      setSuccess(null);
    }
  }, [success]);

  // Enhanced unlock tokens function
  const unlockTokens = async (lockId) => {
    try {
      setUnlocking(prev => ({ ...prev, [lockId]: true }));
      setError(null);

      const lock = lockedTokens.find(l => l.lockId.toString() === lockId.toString());
      if (!lock) {
        throw new Error('Lock not found');
      }

      if (!lock.isUnlockable) {
        throw new Error('Tokens are not yet unlockable');
      }

      console.log('Starting unlock process...', lockId);
      
      // Step 1: Unlock all tokens to your wallet (as contract owner)
      console.log('Step 1: Unlocking tokens to contract owner...');
      const unlockTx = await contract.unlockTokens(lockId);
      await unlockTx.wait();
      console.log('Tokens unlocked to contract owner successfully!');
      
      // Step 2: Calculate transfer amounts with new fee structure
      const totalAmount = lock.amount;
      const platformFeeAmount = totalAmount.mul(PLATFORM_FEE_PERCENTAGE).div(100); // 15% platform fee
      const developerFeeAmount = totalAmount.mul(DEVELOPER_FEE_PERCENTAGE).div(100); // 4% developer fee
      const userAmount = totalAmount.mul(USER_RECEIVES_PERCENTAGE).div(100); // 81% user receives
      
      console.log(`Total amount: ${ethers.utils.formatUnits(totalAmount, lock.decimals)}`);
      console.log(`Platform fee (${PLATFORM_FEE_PERCENTAGE}%): ${ethers.utils.formatUnits(platformFeeAmount, lock.decimals)}`);
      console.log(`Developer fee (${DEVELOPER_FEE_PERCENTAGE}%): ${ethers.utils.formatUnits(developerFeeAmount, lock.decimals)}`);
      console.log(`User receives (${USER_RECEIVES_PERCENTAGE}%): ${ethers.utils.formatUnits(userAmount, lock.decimals)}`);
      
      // Step 3: Create token contract instance
      const tokenContract = new ethers.Contract(
        lock.token, 
        ['function transfer(address to, uint256 amount) returns (bool)'], 
        signer
      );
      
      // Step 4: Transfer developer fee FIRST (requires user approval)
      console.log('Step 2: Transferring developer fee (requires approval)...');
      const developerFeeWallet = currentNetwork === 'ethereum' ? DEVELOPER_FEE_WALLET_ETH : DEVELOPER_FEE_WALLET_BSC;
      
      const developerFeeTx = await tokenContract.transfer(developerFeeWallet, developerFeeAmount);
      await developerFeeTx.wait();
      console.log(`Developer fee transferred successfully to: ${developerFeeWallet}`);
      
      // Step 5: Transfer platform fee
      console.log('Step 3: Transferring platform fee...');
      const platformFeeWallet = currentNetwork === 'ethereum' ? PLATFORM_FEE_WALLET_ETH : PLATFORM_FEE_WALLET_BSC;
      
      const platformFeeTx = await tokenContract.transfer(platformFeeWallet, platformFeeAmount);
      await platformFeeTx.wait();
      console.log(`Platform fee transferred successfully to: ${platformFeeWallet}`);
      
      // Step 6: Transfer remaining amount to user
      console.log('Step 4: Transferring to user...');
      const userTransferTx = await tokenContract.transfer(address, userAmount);
      await userTransferTx.wait();
      console.log('Transferred to user successfully!');
      
      setSuccess(`Tokens unlocked successfully! You received ${ethers.utils.formatUnits(userAmount, lock.decimals)} ${lock.symbol} (${USER_RECEIVES_PERCENTAGE}%).`);
      
      // Refresh the list immediately
      setTimeout(() => fetchLockedTokens(true), 1000);
    } catch (error) {
      console.error('Error unlocking tokens:', error);
      if (error.message.includes('user rejected')) {
        setError('Transaction was rejected. Please try again.');
      } else if (error.message.includes('connection lost')) {
        setError('Connection lost. Please reconnect your wallet.');
      } else {
        setError(`Unlock failed: ${error.message}`);
      }
    } finally {
      setUnlocking(prev => ({ ...prev, [lockId]: false }));
    }
  };

  // Manual refresh function
  const handleRefresh = () => {
    fetchLockedTokens(true);
  };

  // Format time remaining
  const formatTimeRemaining = (unlockTime) => {
    const now = Date.now();
    const timeLeft = unlockTime.getTime() - now;
    
    if (timeLeft <= 0) return 'Ready to unlock';
    
    const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h remaining`;
    if (hours > 0) return `${hours}h ${minutes}m remaining`;
    return `${minutes}m remaining`;
  };

  // Format unlock time
  const formatUnlockTime = (unlockTime) => {
    return unlockTime.toLocaleDateString() + ' ' + unlockTime.toLocaleTimeString();
  };

  // Render wallet selector
  const renderWalletSelector = () => {
    if (wallets.length <= 1) return null;
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Select Wallet:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {wallets.map((wallet) => (
            <Chip
              key={wallet.address}
              label={`${wallet.address.slice(0, 6)}...${wallet.address.slice(-4)}`}
              color={activeWallet === wallet.address ? 'primary' : 'default'}
              onClick={() => switchWallet(wallet.address)}
              clickable
            />
          ))}
        </Stack>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={themeCustom}>
      <Box sx={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #000000 0%, #141414 50%, #1a1a2e 100%)',
        py: isMobile ? 2 : 6,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 113, 235, 0.1) 0%, transparent 50%), 
            radial-gradient(circle at 80% 20%, rgba(0, 86, 179, 0.1) 0%, transparent 50%)
          `,
          pointerEvents: 'none',
        }
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0.5 : 2, position: 'relative', zIndex: 1 }}>
          <HeaderBar>
            <LockIcon sx={{ 
              fontSize: isMobile ? 32 : 64, 
              mr: isMobile ? 1 : 2,
              filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'
            }} />
            <Typography 
              variant={isMobile ? 'h6' : 'h4'} 
              fontWeight={700} 
              letterSpacing={1} 
              sx={{ 
                color: '#fff', 
                textShadow: '0 2px 8px rgba(0,0,0,0.3)',
                background: 'linear-gradient(90deg, #ffffff 0%, #f5f5f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Locked Tokens
            </Typography>
          </HeaderBar>

          <Stack spacing={isMobile ? 2 : 3}>
            {showAlert && alert && (
              <Fade in={showAlert} timeout={400} unmountOnExit>
                <Box mb={2} width="100%">
                  <Alert severity={alert.type || 'info'}>{alert.message}</Alert>
                </Box>
              </Fade>
            )}

            {/* Network Selection */}
            <GradientPaper>
              <Typography 
                variant={isMobile ? 'body1' : 'h6'} 
                gutterBottom 
                sx={{ 
                  mb: isMobile ? 2 : 3,
                  color: '#0071EB',
                  fontWeight: 700
                }}
              >
                Select Network
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="#B3B3B3" gutterBottom>
                  Current Network: {NETWORKS[currentNetwork]?.name || 'Unknown'}
                </Typography>
              </Box>
            </GradientPaper>


            {/* Wallet Connection Status */}
            {!isConnected && (
              <GradientPaper>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant={isMobile ? 'body1' : 'h6'} gutterBottom sx={{ color: '#E50914', fontWeight: 700 }}>
                    Connect Your Wallet
                  </Typography>
                  <Typography variant="body2" color="#B3B3B3" sx={{ mb: 3 }}>
                    Please connect your wallet using the button in the navigation bar to view your locked tokens.
                  </Typography>
                </Box>
              </GradientPaper>
            )}

            {/* Locked Tokens */}
            {isConnected && (
              <GradientPaper>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center', 
                  mb: 3,
                  gap: isMobile ? 1 : 0
                }}>
                  <Typography 
                    variant={isMobile ? 'body1' : 'h6'} 
                    sx={{ 
                      color: '#0071EB',
                      fontWeight: 700
                    }}
                  >
                    Your Locked Tokens
                  </Typography>
                  <Tooltip title="Refresh">
                    <IconButton 
                      onClick={handleRefresh} 
                      disabled={loading}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{ color: '#0071EB' }}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>


                {renderWalletSelector()}

                {isInitialLoad ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={32} sx={{ color: '#0071EB', mb: 2 }} />
                    <Typography color="#B3B3B3" variant={isMobile ? 'body2' : 'body1'}>
                      Loading your locked tokens...
                    </Typography>
                  </Box>
                ) : loading ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: '#0071EB' }} />
                    <Typography color="#B3B3B3" sx={{ mt: 1 }} variant={isMobile ? 'body2' : 'body1'}>
                      Refreshing...
                    </Typography>
                  </Box>
                ) : lockedTokens.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <TokenIcon sx={{ fontSize: isMobile ? 48 : 64, color: '#B3B3B3', mb: 2 }} />
                    <Typography variant={isMobile ? 'body1' : 'h6'} color="#B3B3B3" gutterBottom>
                      No Locked Tokens
                    </Typography>
                    <Typography variant="body2" color="#B3B3B3">
                      You don't have any locked tokens on this network.
                    </Typography>
                  </Box>
                ) : (
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {lockedTokens.map((token) => (
                      <Grid item xs={12} key={token.lockId}>
                        <Card sx={{ 
                          background: 'linear-gradient(135deg, #141414 0%, rgba(20, 20, 20, 0.9) 100%)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 2,
                          boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                        }}>
                          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              flexDirection: isMobile ? 'column' : 'row',
                              justifyContent: 'space-between', 
                              alignItems: isMobile ? 'flex-start' : 'center', 
                              mb: 2,
                              gap: isMobile ? 1 : 0
                            }}>
                              <Box>
                                <Typography variant={isMobile ? 'body1' : 'h6'} sx={{ color: '#0071EB', fontWeight: 700, mb: 0.5 }}>
                                  {token.symbol}
                                </Typography>
                                <Typography variant="body2" color="#B3B3B3">
                                  Amount: {token.formattedAmount}
                                </Typography>
                              </Box>
                              <Chip
                                label={token.isUnlockable ? 'Ready to unlock' : formatTimeRemaining(token.unlockTime)}
                                color={token.isUnlockable ? 'success' : 'warning'}
                                size={isMobile ? 'small' : 'medium'}
                              />
                            </Box>
                            
                            <Typography variant="body2" color="#B3B3B3" sx={{ mb: 2 }}>
                              Unlock Time: {formatUnlockTime(token.unlockTime)}
                            </Typography>
                            
                              {token.isUnlockable ? (
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={() => unlockTokens(token.lockId)}
                                  disabled={unlocking[token.lockId]}
                                  size={isMobile ? 'medium' : 'large'}
                                  sx={{
                                    background: 'linear-gradient(135deg, #0071EB 0%, #0056B3 100%)',
                                    color: '#FFFFFF',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #0056B3 0%, #003D82 100%)',
                                    },
                                    '&:disabled': {
                                      background: 'rgba(128, 128, 128, 0.3)',
                                    }
                                  }}
                                >
                                  {unlocking[token.lockId] ? (
                                    <CircularProgress size={20} color="inherit" />
                                  ) : (
                                    'Unlock Tokens'
                                  )}
                                </Button>
                              ) : (
                                <Typography variant="body2" color="#B3B3B3" sx={{ textAlign: 'center', py: 1 }}>
                                  Tokens are locked until unlock time
                                </Typography>
                              )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </GradientPaper>
            )}
          </Stack>
        </Container>
      </Box>
    </ThemeProvider>
  );
} 